import { useQuery, useMutation, useQueryClient, UseQueryOptions } from 'react-query'
import { SitesApi } from '@trybeapp/sdk.shop'
import {
  CreateIntegrationConfigRequest,
  GetGiftproConfigResponse,
  GiftproApi,
  GuestlineApi,
  IntegrationConfigResponse,
  IntegrationConfigsApi,
  ListGuestlineAnalysisCodes,
  ListGuestlinePaymentCodes,
  ListIntegrationConfigs,
  ListIntegrationConfigsRequest,
  ResponseError,
  UpdateIntegrationConfigRequest,
} from '@trybeapp/sdk'
import { useCurrentSiteId } from 'contexts/SiteConfig'
import { apiConfiguration } from 'utilities/TrybeApiClient'
import { ApiResponse, LegacySdkResponseError } from 'api/api.types'

const api = new SitesApi()
const integrationsApi = new IntegrationConfigsApi(apiConfiguration)
const giftpro = new GiftproApi(apiConfiguration)
const guestline = new GuestlineApi(apiConfiguration)

export const useGetIntegrationConfigs = (
  params: ListIntegrationConfigsRequest = {},
  options: UseQueryOptions<ListIntegrationConfigs, ResponseError> = {}
) => {
  const siteId = useCurrentSiteId()

  return useQuery<ListIntegrationConfigs, ResponseError>(
    ['integrationConfig', { ...params, siteId }],
    () =>
      integrationsApi.listIntegrationConfigs({
        siteId,
        provider: params.provider,
        type: params.type,
        page: params.page,
      }),
    options
  )
}

export const useGetIntegrationConfig = (
  id: string,
  options: UseQueryOptions<IntegrationConfigResponse, ResponseError> = {}
) => {
  return useQuery<IntegrationConfigResponse, ResponseError>(
    ['integrationConfig', id],
    () => integrationsApi.getIntegrationConfig({ integrationConfigId: id }),
    options
  )
}

export const useCreateIntegrationConfig = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<IntegrationConfigResponse, ResponseError, CreateIntegrationConfigRequest>(
    (config) => {
      config.site_id = siteId

      return integrationsApi.createIntegrationConfig({
        createIntegrationConfigRequest: config,
      })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('integrationConfig')
      },
    }
  )
}

export const useUpdateIntegrationConfig = () => {
  const queryClient = useQueryClient()

  return useMutation<
    IntegrationConfigResponse,
    ResponseError,
    [string, UpdateIntegrationConfigRequest]
  >(
    ([configId, config]) =>
      integrationsApi.updateIntegrationConfig({
        integrationConfigId: configId,
        updateIntegrationConfigRequest: config,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('integrationConfig')
      },
    }
  )
}

export const useEnableIntegrationConfig = () => {
  const queryClient = useQueryClient()

  return useMutation<void, ResponseError, string>(
    (configId) =>
      integrationsApi.actionEnableIntegration({
        integrationConfigId: configId,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('integrationConfig')
      },
    }
  )
}

export const useDisableIntegrationConfig = () => {
  const queryClient = useQueryClient()

  return useMutation<void, ResponseError, string>(
    (configId) =>
      integrationsApi.actionDisableIntegration({
        integrationConfigId: configId,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('integrationConfig')
      },
    }
  )
}

export const useGetGuestlineConfig = (
  options?: UseQueryOptions<ApiResponse<Record<string, any>>, LegacySdkResponseError>
) => {
  const siteId = useCurrentSiteId()

  return useQuery<ApiResponse<Record<string, any>>, LegacySdkResponseError>(
    ['guestlineConfig', siteId],
    () => api.siteGuestlineConfigIndex(siteId),
    options
  )
}

export const useGetGuestlinePaymentCodes = (
  options?: UseQueryOptions<ListGuestlinePaymentCodes, ResponseError>
) => {
  const siteId = useCurrentSiteId()

  return useQuery<ListGuestlinePaymentCodes, ResponseError>(
    ['guestlinePaymentCodes', siteId],
    () => guestline.listGuestlinePaymentCodes({ siteId }),
    options
  )
}

export const useGetGuestlineAnalysisCodes = (
  options?: UseQueryOptions<ListGuestlineAnalysisCodes, ResponseError>
) => {
  const siteId = useCurrentSiteId()

  return useQuery<ListGuestlineAnalysisCodes, ResponseError>(
    ['guestlineAnalysisCodes', siteId],
    () => guestline.listGuestlineAnalysisCodes({ siteId }),
    options
  )
}

export const useUpdateGuestlinePaymentTypeMapping = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<ApiResponse<Record<string, any>>, LegacySdkResponseError, [string, string]>(
    ([customPaymentTypeId, paymentCode]) =>
      api.siteGuestlinePaymentTypeMappingUpdate(siteId, customPaymentTypeId, {
        updateGuestlinePaymentTypeMapping: { payment_code: paymentCode },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('guestlineConfig')
      },
    }
  )
}

export const useUpdateGuestlineRevenueCentreMapping = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<ApiResponse, LegacySdkResponseError, [string, string]>(
    ([revenueCentreId, analysisCode]) =>
      api.siteGuestlineRevenueCentreMappingUpdate(siteId, revenueCentreId, {
        updateGuestlineRevenueCentreMapping: { analysis_code: analysisCode },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('guestlineConfig')
      },
    }
  )
}

export const useUpdateGuestlineSettings = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<ApiResponse, LegacySdkResponseError, Record<string, any>>(
    (settings) =>
      api.siteGuestlineSettingsUpdate(siteId, {
        guestlineSettings: settings,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('guestlineConfig')
      },
    }
  )
}

export const useUpdateGuestlineCredentials = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<ApiResponse, LegacySdkResponseError, Record<string, any>>(
    (credentials) =>
      api.siteGuestlineCredentialsUpdate(siteId, {
        guestlineCredentials: credentials,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('guestlineConfig')
      },
    }
  )
}

export const useEnableGuestline = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<ApiResponse, LegacySdkResponseError, null | void>(
    () => api.siteGuestlineEnable(siteId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('guestlineConfig')
      },
    }
  )
}

export const useDisableGuestline = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<ApiResponse, LegacySdkResponseError, void>(
    () => api.siteGuestlineDisable(siteId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('guestlineConfig')
      },
    }
  )
}

export const useUpdatePmsSettings = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<ApiResponse, LegacySdkResponseError, Record<string, any>>(
    (settings) =>
      api.sitePmsSettingsUpdate(siteId, {
        pmsConfig: settings,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('guestlineConfig')
        queryClient.invalidateQueries('mewsConfig')
        queryClient.invalidateQueries('operaFiasConfig')
      },
    }
  )
}

export const useGetGiftProConfig = (
  options?: UseQueryOptions<GetGiftproConfigResponse, ResponseError>
) => {
  const siteId = useCurrentSiteId()

  return useQuery<GetGiftproConfigResponse, ResponseError>(
    ['giftProConfig', siteId],
    () => giftpro.getGiftproConfig({ siteId }),
    options
  )
}

export const useEnableGiftPro = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<ApiResponse, LegacySdkResponseError, void | null>(
    () => api.siteGiftProEnable(siteId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('giftProConfig')
      },
    }
  )
}

export const useDisableGiftPro = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<ApiResponse, LegacySdkResponseError, void>(
    () => api.siteGiftProDisable(siteId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('giftProConfig')
      },
    }
  )
}

export const useUpdateGiftProSettings = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<ApiResponse, LegacySdkResponseError, Record<string, any>>(
    (settings) =>
      api.siteGiftProSettingsUpdate(siteId, {
        updateGiftProSettings: settings,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('giftProConfig')
      },
    }
  )
}

export const useGetAvvioConfig = (
  options?: UseQueryOptions<ApiResponse<Record<string, any>>, LegacySdkResponseError>
) => {
  const siteId = useCurrentSiteId()

  return useQuery<ApiResponse<Record<string, any>>, LegacySdkResponseError>(
    ['avvioConfig', siteId],
    () => api.siteAvvioConfigIndex(siteId),
    options
  )
}

export const useEnableAvvio = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<ApiResponse, LegacySdkResponseError, void | null>(
    () => api.siteAvvioEnable(siteId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('avvioConfig')
      },
    }
  )
}

export const useDisableAvvio = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<ApiResponse, LegacySdkResponseError, void>(
    () => api.siteAvvioDisable(siteId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('avvioConfig')
      },
    }
  )
}

export const useUpdateAvvioSettings = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<ApiResponse, LegacySdkResponseError, Record<string, any>>(
    (settings) =>
      api.siteAvvioSettingsUpdate(siteId, {
        updateAvvioSettings: settings,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('avvioConfig')
      },
    }
  )
}

export const useImportAvvioRoomTypes = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<ApiResponse, LegacySdkResponseError, void | null>(
    () => api.siteAvvioSettingsImportRoomTypes(siteId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('avvioConfig')
      },
    }
  )
}

export const useGetBookingEngineRates = (
  options?: UseQueryOptions<ApiResponse<Array<Record<string, any>>>, LegacySdkResponseError>
) => {
  const siteId = useCurrentSiteId()

  return useQuery<ApiResponse<Array<Record<string, any>>>, LegacySdkResponseError>(
    ['bookingEngineRates', siteId],
    () => api.siteSettingsBookingEngineRatesIndex(siteId),
    options
  )
}

export const useGetBookingEngineAgeCategories = <TData = ApiResponse<Array<Record<string, any>>>>(
  options?: UseQueryOptions<ApiResponse<Array<Record<string, any>>>, LegacySdkResponseError, TData>
) => {
  const siteId = useCurrentSiteId()

  return useQuery<ApiResponse<Array<Record<string, any>>>, LegacySdkResponseError, TData>(
    ['bookingEngineAgeCategories', { siteId }],
    () => api.siteSettingsBookingEngineAgeCategoriesIndex(siteId),
    options
  )
}
